import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';

import { Observable } from 'rxjs';
import { HttpUtils } from '../../shared/http-utils';
import { URL_BASE } from '../../core/constants';
import { SearchObjectResult } from '../../shared/search-object-result.model';
import { ImportFileLoadsResult } from './file-import/import-file-loads-result';
import { Carrier } from '../../user-dashboard/shared/carrier.model';
import { LocalLoad } from './local-load.model';
import { LocalLoadSearchCriteria } from './local-load-search-criteria.model';
import { FileService } from '../../shared/services/file.service';
import * as moment from 'moment';
import { ImportTmsLoadsResult } from './tms-import/import-tms-loads-result';
import { LocalLoadEvent } from './local-load-event.model';
import { LocalLoadCommunication } from './local-load-communication.model';
import { LocalLoadDocument } from './local-load-document.model';
import { flatMap, map } from 'rxjs/operators';
import { ManualLocalLoadCreationRequest } from './manual-local-load-creation';
import { ManualLocalLocationCreationRequest } from './manual-local-location-creation';
import { LocalLocation } from './local-location.model';

@Injectable()
export class LocalLoadService {

  private LOCAL_LOADS_URI = 'local-loads';

  constructor(private http: HttpClient, private fileService: FileService) {
  }

  public getById(localLoadId: string): Observable<LocalLoad> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/${localLoadId}`;

    return this.http.get(url, { headers })
      .pipe(
        map((l: any) => LocalLoad.fromDto(l))
      );
  }

  public getEvents(localLoad: LocalLoad): Observable<LocalLoadEvent[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/${localLoad.id}/events`;

    return this.http.get(url, { headers })
      .pipe(
        map((result: any[]) => result.map(e => LocalLoadEvent.fromDto(e)))
      );
  }

  public getCommunication(localLoad: LocalLoad): Observable<LocalLoadCommunication> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/${localLoad.id}/communication`;

    return this.http.get(url, { headers })
      .pipe(
        map((result: any) => LocalLoadCommunication.fromDto(result))
      );
  }

  public getDocuments(localLoad: LocalLoad): Observable<LocalLoadDocument[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/${localLoad.id}/documents`;

    return this.http.get(url, { headers })
      .pipe(
        map((result: any[]) => result.map(d => LocalLoadDocument.fromDto(d)))
      );
  }

  importLoadsForSelfCarrier(file: File): Observable<ImportFileLoadsResult> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/import/file/for/carrier/self`;

    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(url, formData, { headers })
      .pipe(
        map(result => ImportFileLoadsResult.fromDto(result))
      );
  }

  public importLoadsForCarrier(file: File, carrier: Carrier): Observable<ImportFileLoadsResult> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/import/file/for/carrier/${carrier.id}`;

    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(url, formData, { headers })
      .pipe(
        map(result => ImportFileLoadsResult.fromDto(result))
      );
  }

  public searchForSelfCarrier(
    page: number, size: number, criteria: LocalLoadSearchCriteria
  ) {
    const criteriaCopy = { ...criteria };
    if (criteriaCopy.vehicles != null && criteriaCopy.vehicles.length === 0) {
      criteriaCopy.vehicles = null;
    }
    if (criteriaCopy.status != null && criteriaCopy.status.length === 0) {
      criteriaCopy.status = null;
    }
    if (criteriaCopy.loadNumbers != null && criteriaCopy.loadNumbers.length === 0) {
      criteriaCopy.loadNumbers = null;
    }
    for (const key of Object.keys(criteriaCopy)) {
      if (!criteriaCopy[key] || key === 'source' && criteriaCopy[key] === 'ALL') {
        delete criteriaCopy[key];
      }
    }

    const headers = HttpUtils.createHeaders();
    const params  = {
      page: String(page),
      size: String(size),
      ...criteriaCopy
    };

    const url = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/search/by/carrier/self`;
    return this.http.get(url, { headers, params })
      .pipe(
        map(result => SearchObjectResult.fromDto(result, LocalLoad.fromDto))
      );
  }

  public searchForCarrier(
    page: number, size: number, carrierId: string, criteria: LocalLoadSearchCriteria
  ) {
    const criteriaCopy = { ...criteria };
    if (criteriaCopy.vehicles != null && criteriaCopy.vehicles.length === 0) {
      criteriaCopy.vehicles = null;
    }
    if (criteriaCopy.status != null && criteriaCopy.status.length === 0) {
      criteriaCopy.status = null;
    }
    if (criteriaCopy.loadNumbers != null && criteriaCopy.loadNumbers.length === 0) {
      criteriaCopy.loadNumbers = null;
    }
    for (const key of Object.keys(criteriaCopy)) {
      if (!criteriaCopy[key] || key === 'source' && criteriaCopy[key] === 'ALL') {
        delete criteriaCopy[key];
      }
    }

    const headers = HttpUtils.createHeaders();
    const params  = {
      page: String(page),
      size: String(size),
      ...criteriaCopy
    };

    const url = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/search/by/carrier/${carrierId}`;
    return this.http.get(url, { headers, params })
      .pipe(
        map(result => SearchObjectResult.fromDto(result, LocalLoad.fromDto))
      );
  }

  public searchReorderForSelfCarrier(
    page: number, size: number, criteria: LocalLoadSearchCriteria
  ) {
    const criteriaCopy = { ...criteria };
    if (criteriaCopy.vehicles != null && criteriaCopy.vehicles.length === 0) {
      criteriaCopy.vehicles = null;
    }
    for (const key of Object.keys(criteriaCopy)) {
      if (!criteriaCopy[key]) {
        delete criteriaCopy[key];
      }
    }

    const headers = HttpUtils.createHeaders();
    const params  = {
      page: String(page),
      size: String(size),
      sort: 'POSITION',
      ...criteriaCopy
    };

    const url = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/search/by/carrier/self`;
    return this.http.get(url, { headers, params })
      .pipe(
        map((result: any) => {
          if (criteria.showCompleted) {
            result.results = result.results.filter(load => {
              return !(load.completed_at !== null &&
                !moment(load.completed_at).isBetween(moment().subtract(7, 'days'), moment(), null, '[]'));
            });
          }
          return SearchObjectResult.fromDto(result, LocalLoad.fromDto);
        })
      );
  }

  public searchForReorderCarrier(
    page: number, size: number, carrierId: string, criteria: LocalLoadSearchCriteria
  ) {
    const criteriaCopy = { ...criteria };
    if (criteriaCopy.vehicles != null && criteriaCopy.vehicles.length === 0) {
      criteriaCopy.vehicles = null;
    }
    for (const key of Object.keys(criteriaCopy)) {
      if (!criteriaCopy[key]) {
        delete criteriaCopy[key];
      }
    }

    const headers = HttpUtils.createHeaders();
    const params  = {
      page: String(page),
      size: String(size),
      sort: 'POSITION',
      ...criteriaCopy
    };

    const url = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/search/by/carrier/${carrierId}`;
    return this.http.get(url, { headers, params })
      .pipe(
        map((result: any) => {
          if (criteria.showCompleted) {
            result.results = result.results.filter(load => {
              return !(load.completed_at !== null &&
                !moment(load.completed_at).isBetween(moment().subtract(7, 'days'), moment(), null, '[]'));
            });
          }
          return SearchObjectResult.fromDto(result, LocalLoad.fromDto);
        })
      );
  }

  public downloadPod(podId: string): Observable<void> {
    const url = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/documents/pod/${podId}/download`;

    const headers = HttpUtils.createHeaders();
    headers.set('Accept', 'text/plain');

    return this.http.get(url, { headers, responseType: 'blob', observe: 'response' })
      .pipe(
        flatMap((response: HttpResponse<Blob>) => this.fileService.saveToFileSystem(response))
      );
  }

  public getPictureRootUrl(pictureId: string): string {
    return `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/documents/picture/${pictureId}`;
  }

  public downloadPicture(pictureId: string): Observable<void> {
    const url = `${this.getPictureRootUrl(pictureId)}/download`;

    const headers = HttpUtils.createHeaders();
    headers.set('Accept', 'text/plain');

    return this.http.get(url, { headers, responseType: 'blob', observe: 'response' })
      .pipe(
        flatMap((response: HttpResponse<Blob>) => this.fileService.saveToFileSystem(response))
      );
  }

  public exportLocalLoadsForSelfCarrier(criteria: LocalLoadSearchCriteria) {
    const url = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/export/file/for/carrier/self`;

    const headers = HttpUtils.createHeaders();
    headers.set('Accept', 'text/plain');

    const params = this.searchCriteriatToQueryParams(criteria);

    this.http.get(url, { headers, params, responseType: 'blob', observe: 'response' })
      .subscribe((response: HttpResponse<Blob>) => this.fileService.saveToFileSystem(response));
  }

  public exportLocalLoadsForCarrier(carrier: Carrier, criteria: LocalLoadSearchCriteria) {
    const url = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/export/file/for/carrier/${carrier.id}`;

    const headers = HttpUtils.createHeaders();
    headers.set('Accept', 'text/plain');

    const params = this.searchCriteriatToQueryParams(criteria);

    this.http.get(url, { headers, params, responseType: 'blob', observe: 'response' })
      .subscribe((response: HttpResponse<Blob>) => this.fileService.saveToFileSystem(response));
  }

  public synchronizeTmsLoadsForSelfCarrier(): Observable<ImportTmsLoadsResult> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/synchronize/tms/carrier/self`;

    return this.http.post(url, {}, { headers })
      .pipe(
        map(result => ImportTmsLoadsResult.fromDto(result))
      );
  }

  public synchronizeTmsLoadsForCarrier(carrier: Carrier): Observable<ImportTmsLoadsResult> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/synchronize/tms/carrier/${carrier.id}`;

    return this.http.post(url, {}, { headers })
      .pipe(
        map(result => ImportTmsLoadsResult.fromDto(result))
      );
  }

  public getVehiclesForSelfCarrier(): Observable<string[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/search/vehicles/by/carrier/self`;

    return this.http.get<string[]>(url, { headers })
      .pipe(
        map(result => result)
      );
  }


  public getVehiclesForCarrier(carrierId: string): Observable<string[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.LOCAL_LOADS_URI}/search/vehicles/by/carrier/${carrierId}`;

    return this.http.get<string[]>(url, { headers });
  }

  public assignLoadsToVehicleForSelfCarrier(loadUuids: Array<string>, vehicle: string): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/loads/vehicle/for/carrier/self`;

    return this.http.post<void>(url, { load_uuids: loadUuids, vehicle }, { headers });
  }

  public assignLoadsToVehicleForCarrier(carrierId: string, loadUuids: Array<string>, vehicle: string): Observable<any> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/loads/vehicle/for/carrier/${carrierId}`;

    return this.http.post<void>(url, { load_uuids: loadUuids, vehicle }, { headers });
  }

  public unassignLoadsFromVehicleForSelfCarrier(loadUuids: Array<string>): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/loads/vehicle/for/carrier/self`;

    return this.http.post<void>(url, { load_uuids: loadUuids, vehicle: null }, { headers });
  }

  public unassignLoadsFromVehicleForCarrier(carrierId: string, loadUuids: Array<string>): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/loads/vehicle/for/carrier/${carrierId}`;

    return this.http.post<void>(url, { load_uuids: loadUuids, vehicle: null }, { headers });
  }


  public reorderLoadForSelfCarrier(vehicle: string, localLoadIds: Array<string>, position: number): Observable<any> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-loads/reorder/for/carrier/self`;

    return this.http.post(url, { vehicle, local_load_ids: localLoadIds, position }, { headers })
      .pipe(
        map(result => result)
      );
  }

  public reorderLoadForCarrier(carrierId: string, vehicle: string, localLoadIds: Array<string>, position: number): Observable<any> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-loads/reorder/for/carrier/${carrierId}`;

    return this.http.post(url, { vehicle, local_load_ids: localLoadIds, position }, { headers })
      .pipe(
        map(result => result)
      );
  }

  public orderLoadForToTopSelfCarrier(vehicle: string, localLoadIds: Array<string>): Observable<any> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-loads/reorder/for/carrier/self/top`;

    return this.http.post(url, { vehicle, local_load_ids: localLoadIds }, { headers })
      .pipe(
        map(result => result)
      );
  }

  public orderLoadToTopForCarrier(carrierId: string, vehicle: string, localLoadIds: Array<string>): Observable<any> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-loads/reorder/for/carrier/${carrierId}/top`;

    return this.http.post(url, { vehicle, local_load_ids: localLoadIds }, { headers })
      .pipe(
        map(result => result)
      );
  }

  public orderLoadToBottomForSelfCarrier(vehicle: string, localLoadIds: Array<string>): Observable<any> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-loads/reorder/for/carrier/self/bottom`;

    return this.http.post(url, { vehicle, local_load_ids: localLoadIds }, { headers })
      .pipe(
        map(result => result)
      );
  }

  public orderLoadToBottomForCarrier(carrierId: string, vehicle: string, localLoadIds: Array<string>): Observable<any> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-loads/reorder/for/carrier/${carrierId}/bottom`;

    return this.http.post(url, { vehicle, local_load_ids: localLoadIds }, { headers })
      .pipe(
        map(result => result)
      );
  }

  public createManualLocalLoad(carrierId: string, request: ManualLocalLoadCreationRequest): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-loads/manual/creation/for/carrier/${carrierId}`;

    return this.http.post<void>(url, request.toDto(), { headers })
  }

  public resetLocalLoadStatus(id: string): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-loads/${id}/reset-status`;

    return this.http.post<void>(url, {}, { headers });
  }

  private searchCriteriatToQueryParams(criteria: LocalLoadSearchCriteria) {
    const criteriaCopy = { ...criteria };

    for (const key of Object.keys(criteria)) {

      if (!criteriaCopy[key]) {
        delete criteria[key];
      }
    }

    const headers = HttpUtils.createHeaders();
    headers.set('Accept', 'text/plain');

    return {
      ...criteria
    };
  }

}
